const requiredElement = <T extends HTMLElement>(selector: string): T => {
  const element = document.querySelector<T>(selector)
  if (!element) throw new Error(`Missing required action-prompt UI element: ${selector}`)
  return element
}

/** A single, player-confirmed story action presented at screen center. */
export class ActionPrompt {
  private readonly overlay = requiredElement<HTMLElement>('#story-action-prompt')
  private readonly button = requiredElement<HTMLButtonElement>('#story-action-prompt-button')
  private resolvePending: (() => void) | null = null

  public constructor() {
    this.button.addEventListener('click', this.handleClick)
  }

  public show(label: string): Promise<void> {
    this.finishPending()
    this.button.textContent = label.trim()
    this.overlay.dataset.visible = 'true'
    this.overlay.setAttribute('aria-hidden', 'false')

    window.requestAnimationFrame(() => {
      if (this.overlay.dataset.visible === 'true') this.button.focus({ preventScroll: true })
    })

    return new Promise((resolve) => {
      this.resolvePending = resolve
    })
  }

  public hide(): void {
    this.overlay.dataset.visible = 'false'
    this.overlay.setAttribute('aria-hidden', 'true')
  }

  private readonly handleClick = (): void => {
    this.hide()
    this.finishPending()
  }

  private finishPending(): void {
    const resolve = this.resolvePending
    this.resolvePending = null
    resolve?.()
  }

  public dispose(): void {
    this.button.removeEventListener('click', this.handleClick)
    this.hide()
    this.finishPending()
  }
}
